import React, { useEffect, useState } from "react";
import { Music4, Loader2, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const statusColor = {
  pending: "text-[#E6B34A] bg-[#E6B34A]/10",
  played: "text-emerald-400 bg-emerald-400/10",
  rejected: "text-[#FF3B30] bg-[#FF3B30]/10",
};

export default function Requests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [song, setSong] = useState("");
  const [artist, setArtist] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/requests").then((res) => { setRequests(res.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!song.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/requests", { song, artist, message });
      toast.success("Request sent!");
      setSong(""); setArtist(""); setMessage("");
      load();
    } catch (e) { toast.error("Could not send"); } finally { setSubmitting(false); }
  };

  const setStatus = async (id, status) => {
    await api.put(`/requests/${id}`, { status });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/requests/${id}`); toast.success("Request deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const saveEdit = async () => {
    if (!editing.song.trim()) { toast.error("Song title is required"); return; }
    try {
      await api.put(`/requests/${editing.id}`, { song: editing.song, artist: editing.artist, message: editing.message });
      toast.success("Request updated");
      setEditing(null); load();
    } catch (e) { toast.error("Could not update"); }
  };

  const isAdmin = user && user.role === "admin";

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10">
      <div className="flex items-center gap-3 mb-1">
        <Music4 className="w-5 h-5 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Song Requests</h1>
      </div>
      <p className="text-zinc-400 mb-8">{isAdmin ? "Incoming requests from listeners." : "Ask the DJ to play your favorite worship song."}</p>

      <div className="grid lg:grid-cols-5 gap-8">
        {!isAdmin && (
          <form onSubmit={submit} className="lg:col-span-2 space-y-4 rounded-2xl border border-white/5 bg-[#141C42] p-6 h-fit" data-testid="request-form">
            <div>
              <label className="overline text-zinc-400">Song title</label>
              <Input value={song} onChange={(e) => setSong(e.target.value)} placeholder="Way Maker" data-testid="req-song" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
            </div>
            <div>
              <label className="overline text-zinc-400">Artist</label>
              <Input value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="Sinach" data-testid="req-artist" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
            </div>
            <div>
              <label className="overline text-zinc-400">Message</label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Dedication..." data-testid="req-msg" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
            </div>
            <button type="submit" disabled={submitting} data-testid="req-submit" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
              {submitting ? "Sending..." : "Send Request"}
            </button>
          </form>
        )}

        <div className={isAdmin ? "lg:col-span-5" : "lg:col-span-3"}>
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>
          ) : requests.length === 0 ? (
            <p className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl" data-testid="requests-empty">No requests yet.</p>
          ) : (
            <div className="space-y-3" data-testid="requests-list">
              {requests.map((r) => (
                <div key={r.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-lg tracking-tight">{r.song}</h3>
                      {r.artist && <span className="text-sm text-zinc-500">· {r.artist}</span>}
                    </div>
                    {r.message && <p className="text-sm text-zinc-400 mt-1">{r.message}</p>}
                    {isAdmin && <p className="text-xs text-zinc-600 mt-2">by {r.user_name}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`overline px-2.5 py-1 rounded-full ${statusColor[r.status] || statusColor.pending}`}>{r.status}</span>
                    {isAdmin ? (
                      <div className="flex gap-1.5">
                        <button onClick={() => setStatus(r.id, "played")} data-testid={`played-${r.id}`} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20">Played</button>
                        <button onClick={() => setStatus(r.id, "rejected")} data-testid={`reject-${r.id}`} className="text-xs px-2.5 py-1 rounded-lg bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20">Reject</button>
                        <button onClick={() => del(r.id)} data-testid={`req-del-${r.id}`} title="Delete" className="text-xs px-2 py-1 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <div className="flex gap-1.5">
                        <button onClick={() => setEditing({ id: r.id, song: r.song, artist: r.artist || "", message: r.message || "" })} data-testid={`req-edit-${r.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                        <button onClick={() => del(r.id)} data-testid={`req-del-${r.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="request-edit-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">Edit Request</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <div>
                <label className="overline text-zinc-400">Song title</label>
                <Input value={editing.song} onChange={(e) => setEditing({ ...editing, song: e.target.value })} data-testid="edit-req-song" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              </div>
              <div>
                <label className="overline text-zinc-400">Artist</label>
                <Input value={editing.artist} onChange={(e) => setEditing({ ...editing, artist: e.target.value })} data-testid="edit-req-artist" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              </div>
              <div>
                <label className="overline text-zinc-400">Message</label>
                <Textarea value={editing.message} onChange={(e) => setEditing({ ...editing, message: e.target.value })} data-testid="edit-req-msg" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              </div>
              <button onClick={saveEdit} data-testid="save-req-edit" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">Save changes</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
